import React, { useState, useReducer, useEffect, useCallback, useMemo } from 'react';
import { useDebounce } from '../hooks/useDebounce.js';
import GameList from './GameList.jsx';
import GameFilters from './GameFilters.jsx';
import GameModal from './GameModal';
import GameGroupModal from './GameGroupModal.jsx';
import EditGameModal from './EditGameModal';
import NewGameGroupModal from './NewGameGroupModal.jsx';
import EditGameGroupModal from './EditGameGroupModal.jsx';
import BackToTopButton from './BackToTopButton.jsx';
import BulkImportModal from './BulkImportModal';
import Header from './Header.jsx';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext';
import EditProfileModal from './EditProfileModal.jsx';
import ChangePasswordModal from './ChangePasswordModal.jsx';
import LoginModal from './LoginModal';
import ForgotPasswordModal from './ForgotPasswordModal.jsx';
import SignUpModal from './SignUpModal.jsx';

const initialFilterState = {
  searchTerm: '',
  playerCount: '',
  selectedCategory: '',
  sortBy: 'title',
  sortDirection: 'asc',
};

function filterReducer(state, action) {
  switch (action.type) {
    case 'SET_FILTER':
      return { ...state, [action.payload.name]: action.payload.value };
    case 'SET_ALL_FILTERS':
      return { ...state, ...action.payload };
    case 'TOGGLE_SORT_DIRECTION':
      return { ...state, sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc' };
    case 'RESET_FILTERS':
      return { ...initialFilterState };
    default:
      throw new Error();
  }
}

const uiInitialState = {
  activeModal: null, // 'login', 'signup', 'forgotPassword', 'editProfile', 'changePassword', 'newGroup', 'bulkImport'
  selectedGameId: null,
  selectedGroup: null,
  lastSelectedGroup: null,
  editingGameId: null,
  editingGroupId: null,
};

function uiReducer(state, action) {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { ...uiInitialState, activeModal: action.payload };
    case 'CLOSE_MODAL':
      return { ...uiInitialState };
    case 'SELECT_GAME':
      return { ...uiInitialState, selectedGameId: action.payload };
    case 'SELECT_GROUP':
      return { ...uiInitialState, selectedGroup: action.payload };
    case 'EDIT_GAME':
      return { ...uiInitialState, editingGameId: action.payload };
    case 'EDIT_GROUP':
      return { ...uiInitialState, editingGroupId: action.payload };
    case 'BACK_TO_GROUP':
      return { ...uiInitialState, selectedGroup: state.lastSelectedGroup };
    case 'SELECT_GAME_FROM_GROUP':
      return { ...uiInitialState, selectedGameId: action.payload, lastSelectedGroup: state.selectedGroup };
    case 'SWITCH_MODAL':
      return { ...state, activeModal: action.payload };
    case 'CLOSE_ITEM_MODALS':
      return { ...state, selectedGameId: null, selectedGroup: null, editingGameId: null, editingGroupId: null, lastSelectedGroup: null };
    default:
      return state;
  }
}

const initFiltersFromURL = (initialState) => {
  const params = new URLSearchParams(window.location.search);
  return {
    searchTerm: params.get('search') || initialState.searchTerm,
    playerCount: params.get('players') || initialState.playerCount,
    selectedCategory: params.get('category') || initialState.selectedCategory,
    sortBy: params.get('sort') || initialState.sortBy,
    sortDirection: params.get('direction') || initialState.sortDirection,
  };
};

const Homepage = () => {
  const [filters, dispatchFilters] = useReducer(filterReducer, initialFilterState, initFiltersFromURL);
  const [uiState, dispatchUi] = useReducer(uiReducer, uiInitialState);
  const { activeModal, selectedGameId, selectedGroup, lastSelectedGroup, editingGameId, editingGroupId } = uiState;

  const [allCategories, setAllCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isListLoading, setIsListLoading] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [editModalRefreshCounter, setEditModalRefreshCounter] = useState(0);
  const { user } = useUser();
  const { addToast } = useToast();

  const debouncedSearchTerm = useDebounce(filters.searchTerm, 500);
  const debouncedPlayerCount = useDebounce(filters.playerCount, 500);

  const debouncedFilters = useMemo(() => ({
    searchTerm: debouncedSearchTerm,
    playerCount: debouncedPlayerCount,
    selectedCategory: filters.selectedCategory,
    sortBy: filters.sortBy,
    sortDirection: filters.sortDirection,
  }), [debouncedSearchTerm, debouncedPlayerCount, filters.selectedCategory, filters.sortBy, filters.sortDirection]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedFilters.searchTerm) params.set('search', debouncedFilters.searchTerm);
    if (debouncedFilters.playerCount) params.set('players', debouncedFilters.playerCount);
    if (debouncedFilters.selectedCategory) params.set('category', debouncedFilters.selectedCategory);
    if (debouncedFilters.sortBy !== initialFilterState.sortBy) params.set('sort', debouncedFilters.sortBy);
    if (debouncedFilters.sortDirection !== initialFilterState.sortDirection) params.set('direction', debouncedFilters.sortDirection);

    const url = new URL(window.location);
    url.search = params.toString();

    window.history.replaceState({ path: url.href }, '', url.href);
  }, [debouncedFilters]);

  useEffect(() => {
    const handlePopState = () => {
      const newFilters = initFiltersFromURL(initialFilterState);
      dispatchFilters({ type: 'SET_ALL_FILTERS', payload: newFilters });
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('/api/v1/categories.json');
        const data = await response.json();
        setAllCategories(data);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleCardClick = useCallback((game) => {
    if (game.isGroup) {
      dispatchUi({ type: 'SELECT_GROUP', payload: game });
    } else {
      dispatchUi({ type: 'SELECT_GAME', payload: game.id });
    }
  }, []);

  const handleCloseItemModals = useCallback(() => dispatchUi({ type: 'CLOSE_ITEM_MODALS' }), []);
  const handleCloseAuthModals = useCallback(() => dispatchUi({ type: 'CLOSE_MODAL' }), []);

  const handleBackToGroup = useCallback(() => dispatchUi({ type: 'BACK_TO_GROUP' }), []);

  const handleEditGroupClick = useCallback((groupId) => dispatchUi({ type: 'EDIT_GROUP', payload: groupId }), []);
  const handleEditClick = useCallback((gameId) => dispatchUi({ type: 'EDIT_GAME', payload: gameId }), []);

  const handleGroupUpdate = useCallback((updatedGroup) => {
    addToast(`Group "${updatedGroup.name}" updated successfully!`, "success");
    setRefreshCounter(c => c + 1);
    handleCloseItemModals();
  }, [addToast, handleCloseItemModals]);

  const handleGameUpdate = useCallback((updatedGame) => {
    addToast("Game updated successfully!", "success");
    setRefreshCounter(c => c + 1);
    handleCloseItemModals();
  }, [addToast, handleCloseItemModals]);

  const handleGameDelete = useCallback((deletedGameId) => {
    addToast("Game deleted successfully.", "success");
    setRefreshCounter(c => c + 1);
    handleCloseItemModals();
  }, [addToast, handleCloseItemModals]);

  const handleGroupCreate = useCallback((newGroup) => {
    addToast(`Group "${newGroup.name}" created successfully!`, "success");
    setEditModalRefreshCounter(c => c + 1);
    handleCloseAuthModals();
  }, [addToast, handleCloseAuthModals]);

  const handleEditFromModal = useCallback((gameId) => dispatchUi({ type: 'EDIT_GAME', payload: gameId }), []);
  const handleGameSelect = useCallback((gameId) => dispatchUi({ type: 'SELECT_GAME_FROM_GROUP', payload: gameId }), []);

  const handleFilterChange = useCallback((e) => {
    dispatchFilters({ type: 'SET_FILTER', payload: { name: e.target.name, value: e.target.value } });
  }, []);

  const handleResetFilters = useCallback(() => dispatchFilters({ type: 'RESET_FILTERS' }), []);

  const handleCategoryClick = useCallback((category) => {
    dispatchFilters({ type: 'SET_FILTER', payload: { name: 'selectedCategory', value: category } });
  }, []);

  const handleLoadingChange = useCallback((isLoading) => setIsListLoading(isLoading), []);
  const handleSortDirectionToggle = useCallback(() => dispatchFilters({ type: 'TOGGLE_SORT_DIRECTION' }), []);

  const isAdmin = user && user.admin;

  const handleTotalCountChange = useCallback((count) => setTotalCount(count), []);

  return (
    <>
      <Header
        onLoginClick={() => dispatchUi({ type: 'OPEN_MODAL', payload: 'login' })}
        onSignUpClick={() => dispatchUi({ type: 'OPEN_MODAL', payload: 'signup' })}
        onEditProfileClick={() => dispatchUi({ type: 'OPEN_MODAL', payload: 'editProfile' })}
        onChangePasswordClick={() => dispatchUi({ type: 'OPEN_MODAL', payload: 'changePassword' })}
        isAdmin={isAdmin}
        onBulkImportClick={() => dispatchUi({ type: 'OPEN_MODAL', payload: 'bulkImport' })}
        onNewGroupClick={() => dispatchUi({ type: 'OPEN_MODAL', payload: 'newGroup' })}
        onLogoClick={handleResetFilters}
      />
      <main className="container homepage-content">
        <h1>Board Game Collection ({isListLoading ? '...' : totalCount} found)</h1>
        <GameFilters
          filters={filters}
          allCategories={allCategories}
          isListLoading={isListLoading}
          onFilterChange={handleFilterChange}
          onSortDirectionToggle={handleSortDirectionToggle}
          onResetFilters={handleResetFilters}
        />
        <GameList
          key={refreshCounter}
          filters={debouncedFilters}
          onCardClick={handleCardClick}
          onEditClick={handleEditClick}
          isAdmin={isAdmin}
          onTotalCountChange={handleTotalCountChange}
          onCategoryClick={handleCategoryClick}
          onLoadingChange={handleLoadingChange}
        />
        {selectedGameId && <GameModal gameId={selectedGameId} onClose={handleCloseItemModals} onCategoryClick={handleCategoryClick} onBackToGroup={lastSelectedGroup ? handleBackToGroup : null} onEditClick={isAdmin ? handleEditFromModal : null} />}
        {selectedGroup && <GameGroupModal group={selectedGroup} onClose={handleCloseItemModals} onGameClick={handleGameSelect} onEditClick={isAdmin ? handleEditGroupClick : null} />}
        {editingGameId && <EditGameModal key={editModalRefreshCounter} gameId={editingGameId} onClose={handleCloseItemModals} onGameUpdate={handleGameUpdate} onGameDelete={handleGameDelete} addToast={addToast} />}
        {editingGroupId && <EditGameGroupModal groupId={editingGroupId} onClose={handleCloseItemModals} onUpdate={handleGroupUpdate} />}

        {activeModal === 'newGroup' && <NewGameGroupModal onClose={handleCloseAuthModals} onCreate={handleGroupCreate} />}
        {activeModal === 'bulkImport' && <BulkImportModal onClose={handleCloseAuthModals} addToast={addToast} />}
        {activeModal === 'login' && <LoginModal onClose={handleCloseAuthModals} onSwitchToSignUp={() => dispatchUi({ type: 'SWITCH_MODAL', payload: 'signup' })} onSwitchToForgotPassword={() => dispatchUi({ type: 'SWITCH_MODAL', payload: 'forgotPassword' })} />}
        {activeModal === 'changePassword' && <ChangePasswordModal onClose={handleCloseAuthModals} />}
        {activeModal === 'editProfile' && <EditProfileModal onClose={handleCloseAuthModals} />}
        {activeModal === 'signup' && <SignUpModal onClose={handleCloseAuthModals} onSwitchToLogin={() => dispatchUi({ type: 'SWITCH_MODAL', payload: 'login' })} />}
        {activeModal === 'forgotPassword' && <ForgotPasswordModal onClose={handleCloseAuthModals} onSwitchToLogin={() => dispatchUi({ type: 'SWITCH_MODAL', payload: 'login' })} />}
      </main>
      <BackToTopButton />
    </>
  );
};

export default Homepage;
